import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Button, CircularProgress, IconButton, LinearProgress, Menu, MenuItem, Paper, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { cloneDeep, forEach, get } from "lodash";
import Dropzone from "react-dropzone";
import { acceptedFileTypes } from "../utils/constants";
import utils from "../utils/index";
import AdminDocumentTypeList from "./AdminDocumentTypeList";
import "./DocumentList.css";
import { docTypeIcons } from "./documentTypes";

const StyledPaperPadding = styled(Paper)(({ noPadding }) => ({
    padding: noPadding ? "0" : "0 20px 20px",
    border: "none",
    marginBottom: "20px",
    boxShadow: "0 1px 2px rgba(34, 54, 70, 0.1)",
}));

const StyledPaperHeading = styled("h3")({
    fontSize: "18px",
    margin: "14px 0",
});

const StyledHrDivider = styled("hr")({
    margin: "0 -20px",
    borderTop: "1px solid rgba(0, 0, 0, 0.06)",
});

const StyledUploadButton = styled(Button)({
    color: "#757575",
    fill: "#757575",
    "&:hover": {
        backgroundColor: "transparent",
    },
});

const StyledApprovedBy = styled("div")({
    fontWeight: 600,
});

const StyledMenuItem = styled(MenuItem)({
    fontSize: "14px",
    padding: "1vh 2vw",
    cursor: "pointer",
    "&:hover": {
        backgroundColor: "#ebebeb",
    },
});

const StyledDeleteProgress = styled("div")({
    width: "24px",
    padding: "0 10px",
});

const StyledDocIcon = styled("div")({
    float: "left",
});

const StyledDocName = styled("div")({
    whiteSpace: "nowrap",
    overflow: "hidden",
    display: "block",
    textOverflow: "ellipsis",
    paddingTop: "2px",
    paddingLeft: "5px",
    paddingRight: "1vw",
});

const DocumentList = (props) => {
    const {
        allDocumentTypes,
        alternateTitle,
        anchorEl,
        approveDocument,
        copyDocUrl,
        docApproved,
        docMenuKey,
        documentsUploading,
        documentTypesForParentEntity,
        elementsSaving,
        handleAddDocuments,
        handleMenuClose,
        handlePreviewDocument,
        handleOpenDocumentMenu,
        header,
        noHeader,
        open,
        role,
        savedDocuments,
        terminationDocument,
        toggleDeleteDialog,
        toggleTerminationDoc,
        type,
        noPadding,
        documentAttachmentPermissions,
        disableUploads,
        documentRequirements,
    } = props;

    const handleRemoveDocument = (key, documentType) => {
        toggleDeleteDialog(true, key);

        if (documentType === "Termination Document") {
            toggleTerminationDoc(false);
        }
    };

    const handleApproveDocument = (key) => {
        approveDocument(key);
    };

    const handleCopyDocumentUrl = (key) => {
        copyDocUrl(key);
        handleMenuClose();
    };

    const isDeleteDisabledForDocument = () => {
        return savedDocuments[docMenuKey]?.documentType === null
            ? false
            : !documentAttachmentPermissions?.find(
                  (permission) =>
                      permission.documentTypeKey === savedDocuments[docMenuKey]?.documentType?.documentTypeKey
              )?.canDelete;
    };

    const isOpenActionDisabledForDocument = () => {
        return savedDocuments[docMenuKey]?.documentType === null
            ? false
            : !documentAttachmentPermissions?.find(
                  (permission) =>
                      permission.documentTypeKey === savedDocuments[docMenuKey]?.documentType?.documentTypeKey
              )?.canRead;
    };

    const isApproveEnabled = () => {
        return documentAttachmentPermissions?.find(
            (permission) => permission.documentTypeKey === savedDocuments[docMenuKey]?.documentType?.documentTypeKey
        )?.isMenuApproveOptionVisible;
    };

    const isMenuButtonDisabled = (mediaAssetKey) => {
        const documentType = savedDocuments[mediaAssetKey]?.documentType;
        if (!documentType) return false;

        const permission = documentAttachmentPermissions?.find(
            (p) => p.documentTypeKey === documentType?.documentTypeKey
        );

        return !permission?.canDelete && !permission?.canRead && !permission?.isMenuApproveOptionVisible;
    };

    let documentTypes = cloneDeep(props.documentTypes);
    let dropzoneRef;

    if (documentAttachmentPermissions) {
        if (documentAttachmentPermissions?.length > 0 && documentTypes !== undefined) {
            forEach(Object.keys(documentTypes), (key) => {
                const currKey = documentTypesForParentEntity.find(
                    (document) => document.name === documentTypes[key].display
                );
                if (
                    !documentAttachmentPermissions.find((permission) => permission.documentTypeKey === currKey)
                        ?.canUpload
                ) {
                    delete documentTypes[key];
                }
            });
        }
    }

    return (
        <StyledPaperPadding noPadding={noPadding}>
            <div className="attachedDocumentsWrapper">
                <div className="attachedDocumentsUnresponsive">
                    <div>
                        <StyledPaperHeading>
                            {noHeader ? "" : alternateTitle ? alternateTitle : "Attached Documents"}
                        </StyledPaperHeading>
                    </div>
                    <div>
                        <Dropzone
                            accept={acceptedFileTypes}
                            ref={(node) => {
                                dropzoneRef = node;
                            }}
                            onDrop={(accepted, rejected) => {
                                handleAddDocuments(accepted, rejected, type);
                            }}
                            style={{ display: "none" }}
                        >
                            {({ getInputProps }) => <input {...getInputProps()} />}
                        </Dropzone>
                        <StyledUploadButton
                            disabled={disableUploads}
                            component="span"
                            onClick={() => {
                                dropzoneRef.open();
                            }}
                        >
                            <CloudUploadIcon />
                            &nbsp;&nbsp;Upload
                        </StyledUploadButton>
                    </div>
                </div>
                <div>
                    <StyledHrDivider />
                    <div>
                        <div className="attachedDocumentsContainerHeaderRow">
                            <div className="attachedDocumentsContainerHeader">
                                {header.map((x) => (
                                    <div className={x.extraHeaderClassName}>
                                        <span className="attachedDocumentsContainerHeaderText">{x.name}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="attachedDocumentsContainerHeader hideForSmallerDisplays">
                                {header.map((x) => (
                                    <div className={x.extraHeaderClassName}>
                                        <span className="attachedDocumentsContainerHeaderText">{x.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <StyledHrDivider />
                        <div className={"attachedDocumentsContainer"}>
                            {savedDocuments &&
                                Object.keys(savedDocuments).map((x, i) => {
                                    const document = savedDocuments[x];
                                    if (document["parentEntityType"] && props.parentEntityType) {
                                        if (document["parentEntityType"]["name"] !== props.parentEntityType) {
                                            return null;
                                        }
                                    }
                                    if (
                                        document?.documentType?.name === "Termination Document" &&
                                        get(terminationDocument, "upload", null) === false
                                    ) {
                                        toggleTerminationDoc(true);
                                    }
                                    return (
                                        <div key={`doc-${i}`} className="attachedDocument">
                                            <div className="attachmentIconAndNameContainer">
                                                <div
                                                    className={
                                                        role === "portlandBrokerAdmin"
                                                            ? "attachmentIconAndNamewApproval"
                                                            : "attachmentIconAndName"
                                                    }
                                                >
                                                    <StyledDocIcon>
                                                        {get(
                                                            docTypeIcons,
                                                            get(document, "mediaAssetName", " ")
                                                                .split(".")
                                                                .pop()
                                                                .toLowerCase(),
                                                            docTypeIcons["default"]
                                                        )()}
                                                    </StyledDocIcon>
                                                    <Tooltip
                                                        id={`doc-${i}`}
                                                        placement="top"
                                                        title={
                                                            "Uploaded on " +
                                                            utils.operations.serializeLocalDateTimeForMediaAsset(
                                                                document.auditCreatedTimestamp
                                                            )
                                                        }
                                                    >
                                                        <span data-tooltip-id={`doc-${i}`}>
                                                            <StyledDocName>{document["mediaAssetName"]}</StyledDocName>
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                                <div
                                                    className={
                                                        role === "portlandBrokerAdmin"
                                                            ? "attachmentDocumentTypewApproval"
                                                            : "attachmentDocumentType"
                                                    }
                                                >
                                                    <AdminDocumentTypeList
                                                        {...props}
                                                        parentEntityType={props.parentEntityType}
                                                        preexistingValue={document}
                                                        style={{
                                                            display: props.adminListReady ? "" : "none",
                                                        }}
                                                        mediaAssetKey={x}
                                                        documentTypesEnumList={allDocumentTypes}
                                                        updateDocumentFromAdminList={props.updateDocumentFromAdminList}
                                                        savedDocuments={savedDocuments}
                                                        documentAttachmentPermissions={documentAttachmentPermissions}
                                                        documentRequirements={documentRequirements}
                                                    />
                                                </div>
                                                {role === "portlandBrokerAdmin" && (
                                                    <div className="attachedDocumentApprover">
                                                        <StyledApprovedBy>
                                                            {get(document, "approvedBy", "")}
                                                        </StyledApprovedBy>
                                                        <div>
                                                            {utils.operations.formatDate("MM-DD-YYYY")(
                                                                get(document, "approvedByTimestamp", "")
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="attachedDocumentsDropdownOptionsContainer">
                                                <div className="attachedDocumentsDropdownOptions">
                                                    {get(elementsSaving, `documents.${x}`) === true ? (
                                                        <StyledDeleteProgress>
                                                            <CircularProgress size={20}></CircularProgress>
                                                        </StyledDeleteProgress>
                                                    ) : (
                                                        <IconButton
                                                            style={{ display: "flex" }}
                                                            disabled={isMenuButtonDisabled(x)}
                                                            aria-label="More"
                                                            aria-owns={
                                                                Boolean(document["anchorEl"])
                                                                    ? `long-menu-${x}-${i}`
                                                                    : null
                                                            }
                                                            aria-haspopup="true"
                                                            onClick={(e) => handleOpenDocumentMenu(x, e.currentTarget)}
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    )}
                                                    <Menu
                                                        id={`long-menu-${x}-${i}`}
                                                        anchorEl={anchorEl}
                                                        open={open}
                                                        onClose={handleMenuClose}
                                                    >
                                                        <StyledMenuItem
                                                            disabled={isOpenActionDisabledForDocument()}
                                                            onClick={() =>
                                                                handlePreviewDocument(
                                                                    get(document, "url", null),
                                                                    docMenuKey
                                                                )
                                                            }
                                                        >
                                                            Open in a new tab
                                                        </StyledMenuItem>

                                                        <StyledMenuItem
                                                            disabled={isOpenActionDisabledForDocument()}
                                                            onClick={() => handleCopyDocumentUrl(docMenuKey)}
                                                        >
                                                            Copy Link
                                                        </StyledMenuItem>
                                                        {isApproveEnabled() && (
                                                            <StyledMenuItem
                                                                id={`doc-approve-${x}-${i}`}
                                                                disabled={docApproved}
                                                                onClick={() => handleApproveDocument(docMenuKey)}
                                                            >
                                                                Approve
                                                            </StyledMenuItem>
                                                        )}
                                                        <StyledMenuItem
                                                            onClick={() =>
                                                                handleRemoveDocument(
                                                                    docMenuKey,
                                                                    get(
                                                                        savedDocuments,
                                                                        `${docMenuKey}.documentType.name`
                                                                    )
                                                                )
                                                            }
                                                            disabled={isDeleteDisabledForDocument(docMenuKey)}
                                                        >
                                                            Delete
                                                        </StyledMenuItem>
                                                    </Menu>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                            {documentsUploading
                                ? Object.keys(documentsUploading).map((x) => {
                                      if (get(documentsUploading, x)) {
                                          return (
                                              <div className="uploadProgressBar">
                                                  <LinearProgress style={{ width: "55%", marginRight: "2vw" }} />
                                              </div>
                                          );
                                      } else {
                                          return null;
                                      }
                                  })
                                : null}
                        </div>
                    </div>
                </div>
            </div>
        </StyledPaperPadding>
    );
};

export default DocumentList;
